import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";
import { DashboardLayout } from "./DashboardLayout";
import * as amplitude from '@amplitude/unified';

export const DashboardWrapper: React.FC = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const identifyUser = async (authUser: User) => {
    amplitude.setUserId(authUser.id);

    const { data: profile } = await supabase
      .from("profiles")
      .select("name, email, subscription_plan, profile_role, school")
      .eq("id", authUser.id)
      .maybeSingle();

    if (profile) {
      const identify = new amplitude.Identify();
      identify.set("email", profile.email ?? "");
      identify.set("name", profile.name ?? "");
      identify.set("subscription_plan", profile.subscription_plan ?? "free");
      identify.set("profile_role", profile.profile_role ?? "");
      identify.set("school", profile.school ?? "");
      amplitude.identify(identify);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (event === "SIGNED_OUT" || !session) {
          navigate("/auth/signin");
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (!session) {
        navigate("/auth/signin");
      } else if (session.user) {
        identifyUser(session.user);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!session || !user) {
    return null;
  }

  return <DashboardLayout />;
};
